import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { useThreads } from '@/api/generated';
import type { PaginatedThreadsResponseDataItem } from '@/api/generated.schemas';
import { DataTable } from '@/components/blocks/data-table/data-table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { columns } from '@/routes/(app)/agents/lib/threads-columns';

interface ThreadsTableProps {
  onThreadTracing: (row: Row<PaginatedThreadsResponseDataItem>) => void;
  onThreadFeedback: (row: Row<PaginatedThreadsResponseDataItem>) => void;
}

const ThreadsTable = ({
  onThreadTracing,
  onThreadFeedback,
}: ThreadsTableProps) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data, isLoading } = useThreads({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    order: sorting[0]?.desc ? 'desc' : 'asc',
    orderBy: sorting[0]?.id,
  });

  const table = useReactTable({
    state: {
      pagination,
      sorting,
    },
    data: data?.data ?? [],
    columns: columns({ onThreadTracing, onThreadFeedback }),
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount: Math.ceil(data?.total ?? 0 / pagination.pageSize),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    manualSorting: true,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('feedbackAgent.conversations.title')}</CardTitle>
        <CardDescription>
          {t('feedbackAgent.conversations.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <Spinner /> : <DataTable table={table} />}
      </CardContent>
    </Card>
  );
};

export default ThreadsTable;
